
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { TableConfig, TableColumn, PaginationData, TableState } from '../core/list-layout.types';
import { formatDate, formatDateTime, getNestedProperty } from '../core/list-layout.utils';

@Component
export default class ListLayoutTable extends Vue {
    @Prop({ required: true }) config!: TableConfig;
    @Prop({ type: Array, default: () => [] }) items!: any[];
    @Prop({ type: Boolean, default: false }) loading!: boolean;
    @Prop({ type: Object, default: null }) pagination!: PaginationData | null;
    @Prop({ required: true }) tableState!: TableState;

    selectAll = false;
    localSelectedItems: string[] = [];
    pageSize = this.config.pageSize;
    currentPage = 1;

    mounted() {
        this.localSelectedItems = [...this.tableState.selectedItems];
        this.pageSize = this.tableState.pageSize;
        this.currentPage = this.tableState.currentPage;
    }

    @Watch('tableState.selectedItems')
    onSelectedItemsChange(newItems: string[]) {
        this.localSelectedItems = [...newItems];
        this.updateSelectAll();
    }

    @Watch('items')
    onItemsChange() {
        this.updateSelectAll();
    }

    getItemKey(item: any, index: number): string {
        return item.id || item.uid || `item-${index}`;
    }

    getColumnClass(column: TableColumn): string[] {
        const classes = ['column-header'];
        if (column.sortable !== false && this.config.sortable) {
            classes.push('sortable');
        }
        if (column.align) {
            classes.push(`text-${column.align}`);
        }
        return classes;
    }

    getColumnStyle(column: TableColumn): Record<string, string> {
        const style: Record<string, string> = {};
        if (column.width) {
            style.width = typeof column.width === 'number' ? `${column.width}px` : column.width;
        }
        return style;
    }

    getCellClass(column: TableColumn): string[] {
        const classes: string[] = [];
        if (column.align) {
            classes.push(`text-${column.align}`);
        }
        return classes;
    }

    getRowClass(item: any, index: number): string[] {
        const classes = ['table-row'];
        if (index % 2 === 0) {
            classes.push('row-even');
        } else {
            classes.push('row-odd');
        }
        if (this.isItemSelected(item)) {
            classes.push('row-selected');
        }
        return classes;
    }

    getSortIcon(columnValue: string): string {
        if (this.tableState.sort && this.tableState.sort.column === columnValue) {
            return this.tableState.sort.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down';
        }
        return 'mdi-sort';
    }

    formatCellValue(item: any, column: TableColumn): string {
        const value = getNestedProperty(item, column.value);

        if (value === null || value === undefined) {
            return '';
        }

        switch (column.type) {
            case 'date':
                return formatDate(value);
            case 'datetime':
                return formatDateTime(value);
            case 'boolean':
                return value ? this.$i18n.t('common.yes').toString() : this.$i18n.t('common.no').toString();
            case 'number':
                return value.toString() + (column.suffix || '');
            default:
                return value.toString() + (column.suffix || '');
        }
    }

    handleSort(column: TableColumn) {
        if (column.sortable === false || !this.config.sortable) {
            return;
        }

        let direction: 'asc' | 'desc' = 'asc';

        if (this.tableState.sort && this.tableState.sort.column === column.value) {
            direction = this.tableState.sort.direction === 'asc' ? 'desc' : 'asc';
        }

        this.$emit('update:table-state', {
            sort: {
                column: column.value,
                direction,
            },
        });
    }

    handleRowClick(item: any) {
        this.$emit('item-click', item);
    }

    handleSelectAll(selected: boolean) {
        if (selected) {
            this.localSelectedItems = this.items.map((item) => this.getItemKey(item, 0));
        } else {
            this.localSelectedItems = [];
        }
        this.emitSelectionChange();
    }

    handleItemSelect(item: any, selected: boolean) {
        const itemKey = this.getItemKey(item, 0);
        if (selected) {
            if (!this.localSelectedItems.includes(itemKey)) {
                this.localSelectedItems.push(itemKey);
            }
        } else {
            this.localSelectedItems = this.localSelectedItems.filter((id) => id !== itemKey);
        }
        this.updateSelectAll();
        this.emitSelectionChange();
    }

    isItemSelected(item: any): boolean {
        const itemKey = this.getItemKey(item, 0);
        return this.localSelectedItems.includes(itemKey);
    }

    updateSelectAll() {
        this.selectAll = this.items.length > 0 && this.items.every((item) => this.isItemSelected(item));
    }

    emitSelectionChange() {
        this.$emit('selection-change', this.localSelectedItems);
    }

    handlePageSizeChange(newSize: number) {
        this.$emit('update:table-state', {
            pageSize: newSize,
            currentPage: 1,
        });
    }

    handlePageChange(newPage: number) {
        this.$emit('update:table-state', {
            currentPage: newPage,
        });
    }
}
